import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowTopRightOnSquareIcon, BookOpenIcon } from '@heroicons/react/24/outline';
import { usePageMeta } from '../../hooks/usePageMeta';
import Navigation from '../../components/Navigation';
import Breadcrumb from '../../components/Navigation/Breadcrumb';

const SDCEP_MAIN_CHANGES =
  'https://www.antibioticprophylaxis.sdcep.org.uk/implementation-advice/introduction/main-changes-in-this-edition/';
const SDCEP_HOME = 'https://www.antibioticprophylaxis.sdcep.org.uk/implementation-advice/';
const SDCEP_PATIENTS_AT_RISK =
  'https://www.antibioticprophylaxis.sdcep.org.uk/implementation-advice/patients-at-risk-of-infective-endocarditis/';
const SDCEP_AT_RISK_PROCEDURES =
  'https://www.antibioticprophylaxis.sdcep.org.uk/implementation-advice/advice-for-dental-teams/at-risk-dental-procedures/';
const SDCEP_MANAGEMENT =
  'https://www.antibioticprophylaxis.sdcep.org.uk/implementation-advice/advice-for-dental-teams/management-of-patients-at-increased-risk-of-infective-endocarditis/';
const SDCEP_PRESCRIBING =
  'https://www.antibioticprophylaxis.sdcep.org.uk/implementation-advice/prescribing-advice/';
const SDCEP_DISCUSSION_AID =
  'https://www.antibioticprophylaxis.sdcep.org.uk/supporting-tools/patient-discussion-aid/';

export default function SdcepInfectiveEndocarditisProphylaxis() {
  usePageMeta(
    'SDCEP: infective endocarditis antibiotic prophylaxis (official links) | SmileConsent',
    'Read the Scottish Dental Clinical Effectiveness Programme (SDCEP) second-edition updates on antibiotic prophylaxis against infective endocarditis before treatment planning or consent discussions.',
    'https://smileconsent.co.uk/sdcep-infective-endocarditis-prophylaxis'
  );

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <Breadcrumb
          items={[
            { name: 'Home', href: '/' },
            { name: 'SDCEP IE prophylaxis', href: '/sdcep-infective-endocarditis-prophylaxis' },
          ]}
        />
      </div>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 pt-4">
        <header className="mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
            SDCEP: antibiotic prophylaxis and infective endocarditis
          </h1>
          <p className="mt-4 text-lg text-gray-600 leading-relaxed">
            Before you use SmileConsent for relevant procedures, please read the{' '}
            <strong>official SDCEP implementation advice</strong> in full — especially the{' '}
            <strong>second edition</strong> summary of main changes. This page only points to those
            sources; it is not a substitute for the guidance itself.
          </p>
        </header>

        <div className="rounded-2xl border-2 border-blue-200 bg-blue-50/90 p-6 sm:p-8 mb-10">
          <div className="flex items-start gap-3">
            <BookOpenIcon className="h-8 w-8 text-blue-700 shrink-0" aria-hidden />
            <div>
              <h2 className="text-lg font-bold text-gray-900">Start here (official SDCEP)</h2>
              <p className="mt-2 text-gray-700">
                Main changes in this edition — overview of what is new in the second edition of the
                implementation advice.
              </p>
              <a
                href={SDCEP_MAIN_CHANGES}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 font-semibold text-blue-800 hover:text-blue-950 underline"
              >
                Open &ldquo;Main changes in this edition&rdquo; on SDCEP
                <ArrowTopRightOnSquareIcon className="h-5 w-5 shrink-0" aria-hidden />
              </a>
            </div>
          </div>
        </div>

        <section className="mb-10">
          <h2 className="text-xl font-bold text-gray-900">What the second edition covers (headlines)</h2>
          <p className="mt-3 text-gray-700 leading-relaxed">
            SDCEP lists the following as the main areas updated in the second edition (see their page for
            detail and exact wording):
          </p>
          <ul className="mt-4 list-disc pl-6 text-gray-700 space-y-2">
            <li>Simplified classification of patients at <strong>high</strong> and <strong>moderate</strong> risk of infective endocarditis</li>
            <li>Amendments to cardiac conditions in those risk groups</li>
            <li>
              Amendments to recommendations for antibiotic prophylaxis around{' '}
              <strong>{'\u2018at-risk\u2019 dental procedures'}</strong>
            </li>
            <li>Amendments to management of patients at increased risk</li>
            <li>Amendments to recommended drug regimens (see SDCEP prescribing section)</li>
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-bold text-gray-900">Other useful SDCEP pages</h2>
          <ul className="mt-4 space-y-3">
            {[
              { href: SDCEP_HOME, label: 'Implementation advice (hub)' },
              { href: SDCEP_PATIENTS_AT_RISK, label: 'Patients at risk of infective endocarditis' },
              { href: SDCEP_AT_RISK_PROCEDURES, label: '\u2018At-risk\u2019 dental procedures' },
              { href: SDCEP_MANAGEMENT, label: 'Management of patients at increased risk' },
              { href: SDCEP_PRESCRIBING, label: 'Prescribing advice' },
              { href: SDCEP_DISCUSSION_AID, label: 'Patient discussion aid (supporting tool)' },
            ].map(({ href, label }) => (
              <li key={href}>
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-700 hover:text-blue-900 font-medium inline-flex items-center gap-1.5 underline"
                >
                  {label}
                  <ArrowTopRightOnSquareIcon className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
                </a>
              </li>
            ))}
          </ul>
        </section>

        <section className="rounded-xl border border-gray-200 bg-gray-50 p-5 sm:p-6">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">About this SmileConsent page</h2>
          <p className="mt-2 text-sm text-gray-600 leading-relaxed">
            SmileConsent provides consent workflows and documentation tools. <strong>Clinical decisions</strong>, including whether antibiotic prophylaxis is indicated, must follow{' '}
            <strong>current official guidance</strong>, the individual patient, and advice from the patient&rsquo;s medical or cardiac team where appropriate. Always use the live SDCEP site for authoritative content.
          </p>
          <p className="mt-4 text-sm">
            <Link to="/dental-consent-forms" className="text-blue-700 font-medium hover:underline">
              Back to dental consent forms
            </Link>
          </p>
        </section>
      </article>
    </div>
  );
}
